const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://vendor-booking-api.test/api').replace(
  /\/$/,
  ''
);

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
    reader.readAsDataURL(blob);
  });

const waitForImage = (img) =>
  img.decode
    ? img.decode().catch(() => undefined)
    : new Promise((resolve) => {
        if (img.complete && img.naturalWidth > 0) {
          resolve();
          return;
        }
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });

async function fetchBlobAsDataUrl(fetchUrl, headers = {}) {
  const res = await fetch(fetchUrl, {
    mode: 'cors',
    credentials: 'omit',
    cache: 'force-cache',
    headers,
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  if (!blob || blob.size === 0) return null;
  const type = (blob.type || '').toLowerCase();
  if (type.includes('application/json') || type.includes('text/html')) return null;
  return blobToDataUrl(blob);
}

/**
 * Convert a remote (or relative) image URL into a data URL so html2canvas
 * can embed it without CORS tainting. Falls back to the API media proxy.
 */
export async function imageUrlToDataUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:')) return trimmed;

  // Direct fetch (works when the CDN/S3 bucket sends CORS headers)
  try {
    const dataUrl = await fetchBlobAsDataUrl(trimmed);
    if (dataUrl) return dataUrl;
  } catch {
    /* try proxy */
  }

  // Same-origin API proxy (handles S3 without CORS; public + host-allowlisted)
  try {
    const proxyUrl = `${API_BASE_URL}/media/proxy?url=${encodeURIComponent(trimmed)}`;
    const headers = {};
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) headers.Authorization = `Bearer ${token}`;
    const dataUrl = await fetchBlobAsDataUrl(proxyUrl, headers);
    if (dataUrl) return dataUrl;
  } catch {
    /* give up */
  }

  return null;
}

/**
 * Replace every <img> src in a DOM subtree with an embedded data URL.
 * Removes images that cannot be inlined so PDFs don't show empty boxes.
 */
export async function inlineImagesInElement(root) {
  if (!root?.querySelectorAll) return;

  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.currentSrc || img.getAttribute('src') || '';
      if (!src || src.startsWith('data:')) {
        await waitForImage(img);
        return;
      }

      const dataUrl = await imageUrlToDataUrl(src);
      if (dataUrl) {
        img.removeAttribute('crossorigin');
        img.src = dataUrl;
        await waitForImage(img);
      } else {
        img.remove();
      }
    })
  );
}
